import {useNavigation} from '@react-navigation/native';
import * as React from 'react';
import {Platform} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {FormErrors, getFormValues, reduxForm, SubmissionError, SubmitHandler} from 'redux-form';
import {createNewPassword} from '../../../../redux/actions/login';
import {profileTokenSelector} from '../../../../redux/selectors/profile';
import {Forms} from '../../../../shared/models/forms';
import {ServerValidationError} from '../../../../shared/models/serverValidationError';
import {FormValidationError, validationPipe} from '../../../../shared/pipes';
import {Screens} from '../../../../shared/routing';
import {passwordValidator, passwordValidatorMessage} from '../../../../shared/utils/validators';
import {RecoveryByEmailForm} from './model';
import View from './view';

const getFormErrors = (formData: RecoveryByEmailForm) => {
  return validationPipe(formData, [
    {
      names: 'password',
      error: 'Обязательное поле',
      type: 'required',
    },
    {
      names: 'password',
      error: passwordValidatorMessage,
      type: 'custom',
      validator: passwordValidator,
    },
    {
      names: 'confirm',
      error: 'Обязательное поле',
      type: 'required',
    },
    {
      names: 'confirm,password',
      error: 'Поле не совпадает с паролем',
      type: 'equals',
    },
  ]);
};

interface Props {
  code: string;
  handleSubmit: SubmitHandler<RecoveryByEmailForm, {code: string}, string>;
}

const _ConfirmationChangePassword: React.FC<Props> = ({code, handleSubmit}) => {
  const dispatch = useDispatch();
  const formValues: RecoveryByEmailForm = useSelector((state) =>
    getFormValues(Forms.recoveryByEmailForm)(state),
  ) as RecoveryByEmailForm;
  const isSubmitDisabled = !formValues?.password || !formValues?.confirm;
  const token = useSelector(profileTokenSelector());
  const navigation = useNavigation();
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    if (token) {
      navigation.navigate(Screens.PinCodeAuthentication);
    }
  }, [token]);

  const handleChangePassword = async (formData: RecoveryByEmailForm) => {
    const submissionError = getFormErrors(formData);
    if (submissionError) {
      throw new SubmissionError(submissionError);
    }

    try {
      setSubmitting(true);
      await dispatch(createNewPassword(code, formData.password, false));
    } catch (e: unknown) {
      const error = e as ServerValidationError;
      let errorText = error.data?.[0]?.message ?? error.message ?? 'Ошибка. Попробуйте позже';
      throw new SubmissionError({
        password: new FormValidationError(errorText, true),
      } as FormErrors<any, FormValidationError>);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View
      isSubmitDisabled={isSubmitDisabled || submitting}
      onSubmit={(e: any) => {
        e?.preventDefault();
        if (!isSubmitDisabled) {
          handleSubmit(handleChangePassword)();
        }
      }}
    />
  );
};

export const ConfirmationChangePassword = reduxForm<RecoveryByEmailForm, {code: string}>({
  form: Forms.recoveryByEmailForm,
})(_ConfirmationChangePassword);
